/**
 * Вогонь.
 *
 * Every block has had a `burnRate` for a while and nothing read it. This is
 * what reads it.
 *
 * **Two different questions, and keeping them apart is the whole design.**
 *
 *   - `burnRate` answers *will this catch from the thing next to it*. Straw
 *     and boards do; brick, dressed stone and the ground do not, and no
 *     amount of wanting them to will change that.
 *   - What a вогняний снаряд can set alight is a different question, and the
 *     answer is *anything that is not untouched ground*. Stone and earth are
 *     what the world is made of; everything else is something somebody built
 *     or grew, and a pot of burning resin thrown at it will take.
 *
 * That split is what makes "Запалити кремлін" possible at all. A fortress of
 * brick will never catch from a campfire, and it will burn if you throw
 * enough resin at it - which is the difference between a wall and a weapon.
 *
 * **Fierce fire.** A blaze started by a snapped bolt of resin burns hot for a
 * while, and while it is hot it spreads into things that would never catch on
 * their own. Then it dies down to an ordinary fire that only eats what burns.
 * So one bolt eats a hole in a wall and stops; burning a fortress down means
 * feeding it, which is the campaign rather than one lucky shot.
 *
 * Pure: cells, timers and callbacks. It never touches a world.
 */

import {
  burnRate, isNaturalGround, BlockId, isWater, isFrozen,
} from "../block/registry.js";

/**
 * The most cells that may be alight at once.
 *
 * A ceiling, for the same reason the orcs have one: a forest fire with no
 * limit is a frame rate falling to nothing on a machine whose graphics memory
 * is the system memory. When it is full, new ignitions are refused - the fire
 * is as big as it is going to get until something burns away.
 */
export const FIRE_MAX = 400;

/** How long a fierce blaze stays fierce, in seconds. */
export const FIERCE_SECONDS = 14;

/**
 * How long a block takes to burn away.
 *
 * Straw goes in a moment; a board takes a while; a brick smoulders for the
 * best part of a minute. The last one is a game-balance number and not a
 * physical one: it is what stops a single bolt collapsing a wall, and what
 * makes burning a fortress a thing you do over an evening.
 */
export function fuelFor(id: number): number {
  const rate = burnRate(id);
  if (rate >= 3) return 2.5;
  if (rate === 2) return 6;
  if (rate === 1) return 10;
  // Masonry, gilding, glass: it does not catch on its own, so if it is alight
  // something threw fire at it, and it takes its time going.
  return 26;
}

/**
 * Can a вогняний снаряд set this alight?
 *
 * Everything but the ground it is standing on. Air and water cannot burn;
 * stone, earth, sand, ore and the rest of the untouched world will not; and
 * anything anybody built or grew will.
 */
export function canBeSetAlight(id: number): boolean {
  if (id === BlockId.Air) return false;
  if (isWater(id)) return false;
  // Сніг та лід. Neither is on the untouched-ground list - they are weather,
  // not landscape - so without this a snowfield burned.
  if (isFrozen(id)) return false;
  return !isNaturalGround(id);
}

/** Can this catch from a neighbour on its own? */
export function catchesFire(id: number): boolean {
  if (isFrozen(id)) return false;
  return burnRate(id) > 0;
}

/**
 * How fast fire walks out of a cell, in tries a second.
 *
 * Proportional to how the cell burns, which is the fix for "часто вогонь в
 * соломі яка швидко горить не встигає поширитися". Straw is the whole
 * problem: it has the *shortest* life of anything that burns - two and a
 * half seconds - and it used to get the same 0.9 tries a second as a plank
 * that lasts six. Two tries, each of which picks one of six neighbours at
 * random and usually finds air, and a burning roof went out by itself.
 *
 * Tied to `burnRate` so the two numbers can never drift apart again: what
 * burns fastest spreads fastest, and a straw roof now takes the whole house
 * with it unless somebody puts it out.
 */
export const SPREAD_PER_RATE = 1.1;

/**
 * Both of these are **per neighbour**, per second.
 *
 * A cell makes one attempt at a time and picks one of its six neighbours to
 * make it at, so the loop below runs at six times these numbers. Writing them
 * per neighbour is what makes them mean anything: straw's 3.3 is "this face
 * takes a third of a second to catch", and the roof it is on has one straw
 * neighbour and five faces of air. The first draft used them as the rate of
 * the *attempt* rather than of the face, so five tries in six were spent on
 * air and a burning roof still went out by itself - which is the bug this was
 * written to fix, surviving inside its own fix.
 */

/**
 * And what a fierce blaze manages on masonry, which has no rate at all.
 *
 * Where it always was, once the six faces are counted: a fortress going up is
 * a campaign, not an accident, and nothing here is meant to speed that up.
 */
export const MASONRY_SPREAD = 0.06;

export function spreadRate(id: number): number {
  const rate = burnRate(id);
  return rate > 0 ? rate * SPREAD_PER_RATE : MASONRY_SPREAD;
}

export interface Blaze {
  readonly x: number;
  readonly y: number;
  readonly z: number;
  /** Seconds of fuel left. At zero the block is gone. */
  left: number;
  /** Seconds it stays hot enough to take masonry with it. */
  fierce: number;
}

/** What a step did, so the caller can make a noise about it. */
export interface FireReport {
  /** Cells that burned away this step. */
  readonly consumed: { x: number; y: number; z: number }[];
  /** Cells that caught this step. */
  readonly caught: { x: number; y: number; z: number }[];
}

const NEIGHBOURS: readonly (readonly [number, number, number])[] = [
  [1, 0, 0], [-1, 0, 0], [0, 1, 0], [0, -1, 0], [0, 0, 1], [0, 0, -1],
];

export class Fire {
  private blazes = new Map<string, Blaze>();

  get size(): number {
    return this.blazes.size;
  }

  /** Every cell alight, for drawing. */
  burning(): Blaze[] {
    return [...this.blazes.values()];
  }

  isBurning(x: number, y: number, z: number): boolean {
    return this.blazes.has(key(x, y, z));
  }

  /**
   * Set a cell alight.
   *
   * @param fierce whether this is a bolt of resin rather than a spark. A
   * fierce blaze will take masonry with it; an ordinary one will not.
   * @returns whether it caught.
   */
  light(x: number, y: number, z: number, id: number, fierce: boolean): boolean {
    if (this.blazes.size >= FIRE_MAX) return false;
    const at = key(x, y, z);
    if (this.blazes.has(at)) return false;
    // A spark only takes what burns; a bolt takes anything but the ground.
    if (!(fierce ? canBeSetAlight(id) : catchesFire(id))) return false;
    this.blazes.set(at, {
      x, y, z, left: fuelFor(id), fierce: fierce ? FIERCE_SECONDS : 0,
    });
    return true;
  }

  /**
   * Is a body of this size standing in the fire?
   *
   * Asked of every cell the body occupies rather than of the one its feet are
   * in: a flame at chest height is still a flame, and an animal wider than a
   * block can have one foot in a fire and the rest of it out.
   */
  touching(
    x: number, y: number, z: number, width: number, height: number,
  ): boolean {
    const half = width / 2;
    for (let cx = Math.floor(x - half); cx <= Math.floor(x + half); cx++) {
      for (let cy = Math.floor(y); cy <= Math.floor(y + height); cy++) {
        for (let cz = Math.floor(z - half); cz <= Math.floor(z + half); cz++) {
          if (this.blazes.has(key(cx, cy, cz))) return true;
        }
      }
    }
    return false;
  }

  /** Put one out. Called when the block under it stops being there. */
  douse(x: number, y: number, z: number): void {
    this.blazes.delete(key(x, y, z));
  }

  /**
   * Burn for a while.
   *
   * @param read what is in a cell.
   * @param random so a test can drive the spread rather than hope for it.
   */
  step(
    dt: number,
    read: (x: number, y: number, z: number) => number,
    random: () => number = Math.random,
  ): FireReport {
    const consumed: { x: number; y: number; z: number }[] = [];
    const caught: { x: number; y: number; z: number }[] = [];

    for (const [at, blaze] of [...this.blazes]) {
      // Something else took the block away - a pickaxe, another fire, water.
      // The blaze goes with it rather than burning empty air.
      const here = read(blaze.x, blaze.y, blaze.z);
      // Water and snow both put it out: "нехай не горить в снігу та воді".
      if (here === BlockId.Air || isWater(here) || isFrozen(here)) {
        this.blazes.delete(at);
        continue;
      }

      blaze.left -= dt;
      blaze.fierce = Math.max(0, blaze.fierce - dt);

      /*
       * Spread, before it goes out.
       *
       * A cell tries its neighbours a few times a second; how likely each is
       * to take comes from how readily it burns. A fierce blaze can also take
       * masonry, which is the only way a fortress ever goes up.
       */
      const tries = spreadRate(here) * NEIGHBOURS.length;
      if (random() < tries * dt) {
        const [dx, dy, dz] = NEIGHBOURS[Math.floor(random() * NEIGHBOURS.length)]!;
        const nx = blaze.x + dx, ny = blaze.y + dy, nz = blaze.z + dz;
        const there = read(nx, ny, nz);
        if (this.light(nx, ny, nz, there, blaze.fierce > 0)) {
          caught.push({ x: nx, y: ny, z: nz });
        }
      }

      if (blaze.left <= 0) {
        this.blazes.delete(at);
        consumed.push({ x: blaze.x, y: blaze.y, z: blaze.z });
      }
    }

    return { consumed, caught };
  }
}

function key(x: number, y: number, z: number): string {
  return `${x},${y},${z}`;
}
